import React, { useCallback, useEffect, useState } from 'react';
import { SparklesIcon, ArrowPathIcon, FlagIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import moment from 'moment';
import userStore from '../../store/userStore';
// Del fichero concreto y no del índice: el índice arrastra el cliente axios,
// que en Jest (CRA) revienta por ESM aunque el servicio esté mockeado.
import { getErrorMessage } from '../../services/api/errors';
import {
  getExpedientSummary,
  refreshExpedientSummary,
  reportExpedientSummary,
} from '../../services/expedientSummaryService';

/**
 * Resumen del expediente, encima de las pestañas.
 *
 * Va en dos sitios con la misma posición relativa —el panel de gestión y la
 * ficha— porque el trabajo que ahorra es el mismo: no tener que leer las
 * anotaciones, los correos y meses de WhatsApp para saber por dónde va esto.
 *
 * La variante la decide el backend según el rol. Aquí no se pide ninguna.
 */

const INTERNAL_ROLES = ['admin', 'gestor', 'operator'];

// Estados en los que no hay nada que enseñar. El bloque no se pinta: un
// expediente recién importado no debe mostrar una caja vacía, y menos al
// acreedor.
const EMPTY_STATUSES = [null, undefined, 'insufficient_data', 'failed'];

function SourceLink({ source }) {
  if (!source?.label) return null;
  return (
    <span className='shrink-0 text-[11px] text-violet-700/80 whitespace-nowrap' title={source.label}>
      ↗ {source.label}
    </span>
  );
}

/**
 * La barra de «todavía no hay resumen», solo para uso interno.
 *
 * Dice por qué no lo hay, que es lo que distingue «nadie lo ha pedido» de «se
 * intentó y no salió». Los tres casos llevan botón: en los dos últimos puede
 * haber llegado información nueva desde el intento.
 */
const SIN_RESUMEN_TEXTO = {
  insufficient_data: 'No hay suficiente información en el expediente para resumirlo.',
  failed: 'El último intento de resumir falló.',
};

function SinResumen({ status, refreshing, onGenerate, className }) {
  return (
    <section
      className={`border-b border-slate-200 bg-slate-50 px-4 py-2 ${className}`}
      aria-label='Resumen del expediente'
    >
      <div className='flex flex-wrap items-center gap-2'>
        <span className='inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500'>
          <SparklesIcon className='h-4 w-4' />
          Situación
          <span className='rounded-lg bg-slate-400 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-white'>
            IA
          </span>
        </span>

        <span className='text-[12px] text-slate-500'>
          {SIN_RESUMEN_TEXTO[status] || 'Todavía no se ha resumido este expediente.'}
        </span>

        <button
          type='button'
          onClick={onGenerate}
          disabled={refreshing}
          className='ml-auto inline-flex items-center gap-1.5 rounded-lg border border-violet-300 bg-white px-2.5 py-1 text-[12px] font-semibold text-violet-700 transition-colors hover:bg-violet-50 disabled:opacity-40'
        >
          <ArrowPathIcon className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Generando…' : 'Generar resumen'}
        </button>
      </div>
    </section>
  );
}

function ExpedientSummary({ expedientId, className = '' }) {
  const { user } = userStore();
  const isInternal = INTERNAL_ROLES.includes(user?.role);

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    if (!expedientId) return;
    setLoading(true);
    try {
      setSummary(await getExpedientSummary(expedientId));
      setLoadFailed(false);
    } catch (error) {
      // Un resumen que no carga no debe romper la pantalla ni interrumpir con
      // un toast: el expediente sigue siendo perfectamente usable sin él.
      console.error('No se pudo cargar el resumen del expediente:', error);
      setSummary(null);
      // Y tampoco se ofrece generarlo: si la petición de lectura ha fallado, la
      // de generar va a fallar igual, y el botón solo serviría para dar un
      // error más. «No hay resumen» y «no se ha podido preguntar» se parecen en
      // pantalla y no son lo mismo.
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [expedientId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      setSummary(await refreshExpedientSummary(expedientId));
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setRefreshing(false);
    }
  };

  const handleReport = async () => {
    setRefreshing(true);
    try {
      setSummary(await reportExpedientSummary(expedientId, ''));
      toast.success('Gracias. Lo hemos anotado y regenerado el resumen.');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className={`border-b border-slate-200 bg-violet-50/60 px-4 py-3 ${className}`}>
        <div className='animate-pulse space-y-2'>
          <div className='h-2.5 w-24 rounded-lg bg-violet-200/70'></div>
          <div className='h-2.5 w-3/4 rounded-lg bg-slate-200'></div>
          <div className='h-2.5 w-2/3 rounded-lg bg-slate-200'></div>
        </div>
      </div>
    );
  }

  // 🔴 SIN RESUMEN, A QUIEN PUEDE PEDIRLO SE LE OFRECE PEDIRLO.
  //
  // Antes aquí se devolvía `null` y ya está. El bloque solo existía cuando ya
  // había resumen… y el botón de generarlo vive DENTRO del bloque. O sea que
  // solo se podía pedir lo que ya se tenía.
  //
  // No se notaba porque el primero lo escribía el barrido: el botón era para
  // REgenerar. Al apagar el barrido —para que esto no gaste salvo que alguien
  // lo pida, que es justo lo que queremos— ese supuesto se cae y quedan 4.492
  // expedientes sin forma de pedirlo desde la pantalla.
  //
  // Al acreedor se le sigue sin pintar nada: su variante se genera sola al
  // abrir el expediente, y una caja vacía con un botón que no es suyo solo le
  // daría un problema sin salida.
  if (!summary || EMPTY_STATUSES.includes(summary.status)) {
    if (!isInternal || loadFailed) return null;
    return (
      <SinResumen
        status={summary?.status}
        refreshing={refreshing}
        onGenerate={handleRefresh}
        className={className}
      />
    );
  }

  const { situation, facts = [], next_step: nextStep, generated_at: generatedAt } = summary;
  const stale = summary.stale && isInternal;

  return (
    <section
      className={`border-b border-slate-200 bg-violet-50/60 px-4 py-3 ${className}`}
      aria-label='Resumen del expediente'
    >
      <div className='flex flex-wrap items-center gap-2'>
        <span className='inline-flex items-center gap-1.5 text-[13px] font-semibold text-violet-800'>
          <SparklesIcon className='h-4 w-4' />
          Situación
          <span className='rounded-lg bg-violet-700 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-white'>
            IA
          </span>
        </span>

        <span className='ml-auto flex items-center gap-2 text-[11px] text-slate-500'>
          {generatedAt && <span>{moment(generatedAt).fromNow(true)} · {summary.interactions_count} interacciones</span>}
          {isInternal && (
            <>
              <button
                type='button'
                onClick={handleRefresh}
                disabled={refreshing}
                title='Regenerar el resumen'
                className='text-violet-700 transition-colors hover:text-violet-900 disabled:opacity-40'
              >
                <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                type='button'
                onClick={handleReport}
                disabled={refreshing}
                title='No cuadra con el expediente'
                className='text-slate-400 transition-colors hover:text-slate-600 disabled:opacity-40'
              >
                <FlagIcon className='h-4 w-4' />
              </button>
            </>
          )}
        </span>
      </div>

      {stale && (
        <p className='mt-2 flex flex-wrap items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-2 py-1 text-[12px] text-amber-800'>
          <strong className='font-semibold'>
            {summary.pending_interactions} {summary.pending_interactions === 1 ? 'mensaje nuevo' : 'mensajes nuevos'} sin
            resumir.
          </strong>
          <button
            type='button'
            onClick={handleRefresh}
            disabled={refreshing}
            className='ml-auto font-semibold text-violet-700 hover:text-violet-900 disabled:opacity-40'
          >
            Actualizar
          </button>
        </p>
      )}

      {situation && <p className='mt-2 max-w-3xl text-[13px] leading-relaxed text-slate-800'>{situation}</p>}

      {facts.length > 0 && (
        <ul className='mt-2 space-y-1'>
          {facts.map((fact, index) => (
            <li key={index} className='flex items-baseline gap-2 text-[12.5px] text-slate-700'>
              <span className='font-bold text-violet-700' aria-hidden='true'>
                ·
              </span>
              <span className='min-w-0'>{fact.text}</span>
              <span className='ml-auto'>
                <SourceLink source={fact.source} />
              </span>
            </li>
          ))}
        </ul>
      )}

      {nextStep && (
        <p className='mt-1.5 flex items-baseline gap-2 text-[12.5px] text-slate-700'>
          <span className='font-bold text-violet-700' aria-hidden='true'>
            ·
          </span>
          <span>
            <span className='font-semibold'>{isInternal ? 'Siguiente paso:' : 'Próximo hito:'}</span> {nextStep}
          </span>
        </p>
      )}
    </section>
  );
}

export default ExpedientSummary;
